import styled, { css } from 'styled-components';
import { getResponsiveStyles, MEDIA_QUERY } from '@optimus-web/breakpoints';
import { getSpacingToken, getToken } from '@optimus-web/common';
import { GridItemProps, GridProps } from './types';

const getGridResponsiveStyles = ({ templateAreas, templateColumns, templateRows }: GridProps) => ({
    ...(templateAreas && {
        gridTemplateAreas: templateAreas || 'auto',
    }),
    ...(templateColumns && {
        gridTemplateColumns: templateColumns || 'auto',
    }),
    ...(templateRows && {
        gridTemplateRows: templateRows || 'auto',
    }),
});

export const Grid = styled.div<GridProps>`
    display: ${({ inline }) => (inline ? 'inline-grid' : 'grid')};
    ${({ autoFlow }) => autoFlow && `grid-auto-flow: ${autoFlow}`};
    grid-auto-columns: ${({ autoColumns }) => autoColumns || 'auto'};
    grid-auto-rows: ${({ autoRows }) => autoRows || 'auto'};
    height: ${({ height }) => height || 'auto'};
    ${({ alignItems }) => alignItems && `align-items: ${alignItems}`};
    ${({ justifyItems }) => justifyItems && `justify-items: ${justifyItems}`};
    ${({ alignContent }) => alignContent && `align-content: ${alignContent}`};
    ${({ justifyContent }) => justifyContent && `justify-content: ${justifyContent}`};

    ${({ gap, columnGap, rowGap }) =>
        gap
            ? css`
                  gap: ${getSpacingToken(gap)}px;
              `
            : css`
                  column-gap: ${columnGap ? getSpacingToken(columnGap) : getSpacingToken('200')}px;
                  row-gap: ${rowGap ? getSpacingToken(rowGap) : getSpacingToken('200')}px;
              `}

    ${({ divider, theme }) =>
        divider &&
        css`
            & > div:nth-child(n) {
                border-right: ${divider === 'vertical'
                    ? `
                          ${getToken('borderWidth100')({ theme })}px solid ${getToken('borderStaticPrimary')({ theme })}
                      `
                    : 'none'};
                border-bottom: ${divider === 'horizontal'
                    ? `
                          ${getToken('borderWidth100')({ theme })}px solid ${getToken('borderStaticPrimary')({ theme })}
                      `
                    : 'none'};
            }
            & > div:last-child {
                border-right: ${divider === 'vertical' && 'none'};
                border-bottom: ${divider === 'horizontal' && 'none'};
            }
        `}
    ${({ printTemplateAreas, printTemplateColumns, printTemplateRows }) =>
        (printTemplateAreas || printTemplateColumns || printTemplateRows) &&
        css`
            ${MEDIA_QUERY.print} {
                grid-template-areas: ${printTemplateAreas || 'auto'};
                grid-template-columns: ${printTemplateColumns || 'auto'};
                grid-template-rows: ${printTemplateRows || 'auto'};
            }
        `}
    ${getGridResponsiveStyles}
    ${getResponsiveStyles(getGridResponsiveStyles)}
`;

const getGridItemResponsiveStyles = ({ columnStart, columnEnd, rowStart, rowEnd }: GridItemProps) => ({
    ...(columnStart && {
        gridColumnStart: columnStart,
    }),
    ...(columnEnd && {
        gridColumnEnd: columnEnd,
    }),
    ...(rowStart && {
        gridRowStart: rowStart,
    }),
    ...(rowEnd && {
        gridRowEnd: rowEnd,
    }),
});

export const GridItem = styled.div<GridItemProps>`
    ${({ overflow }) => overflow && `overflow: ${overflow}`};
    ${MEDIA_QUERY.print} {
        overflow: visible;
    }
    ${({ area }) => area && `grid-area: ${area}`};
    ${({ column }) => column && `grid-column: ${column}`};
    ${({ row }) => row && `grid-row: ${row}`};
    ${({ alignSelf }) => alignSelf && `align-self: ${alignSelf}`};
    ${({ justifySelf }) => justifySelf && `justify-self: ${justifySelf}`};
    ${({ minWidth }) => minWidth !== undefined && `min-width: ${minWidth}`};
    ${getGridItemResponsiveStyles}
    ${getResponsiveStyles(getGridItemResponsiveStyles)}
`;
